
import { Observable, of as observableOf, throwError as observableThrowError } from 'rxjs'
import { ajax, AjaxResponse } from 'rxjs/ajax'
import { map, mergeMap } from 'rxjs/operators'
import { accessTokenSelector } from 'services/users/selectors'
import { getState, StoreDispatch, StoreThunkAction, StoreThunkDispatch } from 'store/state'
import authorizedAjax from './ajax'
import { NotAuthorizedException, Tags } from './exceptions/NotAuthorizedException'
import { SignableRequest } from './interfaces'
import { deserialize, serialize } from './transform'

type onRetry<T> = () => Observable<T>
type boundExecute<T> = (zipped: SignableRequest) => Observable<T>

/**
 * Serializes the body if there is any present
 *
 * @param {SignableRequest} request
 * @returns {SignableRequest}
 */
function serializeBody(request: SignableRequest): SignableRequest {
  return request.body
    ? { ...request, body: serialize(request.body) }
    : request
}

/**
 * Ensure the cross domain
 *
 * @param {SignableRequest} request
 * @returns {SignableRequest}
 */
function ensureCrossDomain(request: SignableRequest): SignableRequest {
  return { ...request, crossDomain: true }
}

/**
 *
 */
function serializeAsJson(request: SignableRequest): SignableRequest {
  if (!request.body || request.body instanceof FormData) {
    return request
  }

  if (request.headers && request.headers['Content-Type'] && request.headers['Content-Type'].split(';').shift() !== 'application/json') {
    return request
  }

  return {
    ...request,

    body: typeof request.body === 'string'
      ? request.body
      : JSON.stringify(request.body)
  }
}

/**
 * Executes the ajax request
 *
 * @template T
 * @param {StoreDispatch} dispatch
 * @param {SignableRequest} request
 * @returns
 */
function execute<T>(getState: getState, onRetry: onRetry<T>, request: SignableRequest) {
  const onNotAuthorized = function (message: string, tags: Tags) {
    console.log('[pipe] trying to make a not authorized')
    return new NotAuthorizedException(message, { ...tags })
  }

  const boundDeserialize = function boundDeserialize(response: AjaxResponse<any>) {
    return deserialize(response, onNotAuthorized)
  }

  return authorizedAjax<T>(ajax(request).pipe(map(boundDeserialize)) as Observable<T>, getState, onRetry)
}

/**
 * Creates a bound execute that is partially applied
 *
 * @template T
 * @param {getState} getState
 * @param {onRetry<T>} onRetry
 * @returns {boundExecute<T>}
 */
function createExecute<T>(getState: getState, onRetry: onRetry<T>): boundExecute<T> {
  return function (request: SignableRequest) {
    return execute(getState, onRetry, request)
  }
}

/**
 * Creates a function that will retry the request for left more times
 *
 * @template T
 * @param {SignableRequest} request
 * @param {StoreDispatch} dispatch
 * @param {number} left
 * @returns
 */
function createRetry<T>(request: SignableRequest, dispatch: StoreThunkDispatch, unwrap: typeof identityUnwrap, left: number): onRetry<T> {
  return function retry() {
    if (left <= 0) {
      console.error('[pipe] max retries', request)
      return observableThrowError(() => new Error('Maximum retries reached'))
    }

    console.log('[pipe] retry', request)
    return dispatch(authorizedPipe<T>(request, unwrap, left - 1))
  }
}

function identityUnwrap<T>(wrapped: T) {
  return observableOf(wrapped)
}

function addAuthorization(getState: getState): (request: SignableRequest) => SignableRequest {
  return function (request: SignableRequest) {
    if (request.withAuthorization) {
      const token = accessTokenSelector(getState())

      if (!token) {
        console.log('[pipe] missing access token', request.url)
        throw new NotAuthorizedException('Missing access token')
      }

      const withQuery = request.url.indexOf('?') !== -1
      const authQueryString = `access_token=${encodeURIComponent(token)}`
      if (withQuery) {
        request.url = `${request.url}&${authQueryString}`
      } else {
        request.url = `${request.url}?${authQueryString}`
      }
    }
    return request
  }
}

/**
 * Pipes the request from request to deserialized result
 *
 * @export
 * @template T
 * @param {SignableRequest} request
 * @returns {StoreThunkAction<Observable<T>>}
 */
export function authorizedPipe<T>(
  request: SignableRequest,
  unwrap = identityUnwrap,
  retries = 1
): StoreThunkAction<Observable<T>> {
  return (dispatch: StoreDispatch, getState: getState) => {
    const retryOnce = createRetry<T>(request, dispatch, unwrap, retries)
    const boundExecute = createExecute(getState, retryOnce)
    const addAuth = addAuthorization(getState)

    return observableOf<SignableRequest>(request).pipe(
      map(addAuth),
      map(serializeBody),
      map(serializeAsJson),
      map(ensureCrossDomain),
      mergeMap(boundExecute),
      mergeMap(unwrap))
  }
}

export function unauthorizedPipe<T>(
  request: SignableRequest,
  unwrap = identityUnwrap,
  retries = 1
): StoreThunkAction<Observable<T>> {
  return (dispatch: StoreDispatch, getState: getState) => {
    const retryOnce = createRetry<T>(request, dispatch, unwrap, retries)
    const boundExecute = createExecute(getState, retryOnce)

    return observableOf<SignableRequest>(request).pipe(
      map(serializeBody),
      map(serializeAsJson),
      map(ensureCrossDomain),
      mergeMap(boundExecute),
      mergeMap(unwrap))
  }
}

export default authorizedPipe
